import { useState } from 'react';
import type { FormEvent } from 'react';
import Layout from '@/components/feature/Layout';
import JsonLd from '@/components/base/JsonLd';

const contactInfo = [
  { icon: 'ri-mail-line', label: '邮箱', value: 'hello@xiuxiu-dongmanshe.com' },
  { icon: 'ri-time-line', label: '回复时间', value: '工作日 9:00 - 18:00' },
  { icon: 'ri-chat-smile-3-line', label: '社区', value: '加入讨论社区与我们互动' },
];

const FORM_SUBMIT_ADDR = 'https://readdy.ai/api/form/da240htv7gurs51ef5r0';

export default function Contact() {
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = String(formData.get('contact_alt') || '').trim();

    if (honeypot) {
      setStatus('success');
      return;
    }

    formData.delete('contact_alt');

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (value !== '') payload.append(key, String(value));
    });

    try {
      const res = await fetch(FORM_SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || '';
      const okCode = parsed?.code === 'OK';
      if (res.ok && okCode && !/spam/i.test(serverMsg)) {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
        setFormError(serverMsg && !/spam/i.test(serverMsg) ? serverMsg : '提交失败，请稍后重试或直接发送邮件联系我们。');
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，提交失败，请稍后重试。');
    }
  };

  return (
    <Layout>
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'ContactPage',
          name: '联系我们 - 羞羞动漫社',
          description: '通过联系表单或邮箱联系羞羞动漫社，反馈建议或参与社区互动。',
          url: 'https://mangafor.github.io/contact',
        }}
      />

      <section className="relative overflow-hidden bg-foreground-900 pt-24 md:pt-28 pb-12 md:pb-16 text-center">
        <div className="relative z-10 mx-auto w-full max-w-7xl px-4 md:px-6">
          <h1 className="font-heading text-3xl md:text-5xl text-background-50">联系我们</h1>
          <p className="mx-auto mt-3 max-w-xl text-sm md:text-base text-background-100/90">
            有任何建议、问题或合作想法，欢迎随时联系我们，我们会尽快回复。
          </p>
        </div>
      </section>

      <section className="mx-auto w-full max-w-6xl px-4 md:px-6 py-10 md:py-14">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-5">
          <div className="lg:col-span-2 space-y-4">
            <h2 className="font-heading text-xl md:text-2xl">联系方式</h2>
            {contactInfo.map((c) => (
              <div key={c.label} className="flex items-start gap-4 rounded-2xl border border-background-200 bg-background-50 p-4">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-100 text-primary-700">
                  <i className={`${c.icon} text-xl`}></i>
                </span>
                <div>
                  <p className="text-xs text-foreground-500">{c.label}</p>
                  <p className="mt-0.5 text-sm font-medium text-foreground-800">{c.value}</p>
                </div>
              </div>
            ))}
            <div className="rounded-2xl bg-background-100 p-4 text-sm leading-relaxed text-foreground-600">
              期待你的声音，让羞羞动漫社成为一个更好的同好聚集地。
            </div>
          </div>

          <div className="lg:col-span-3">
            <form
              id="contact-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="rounded-2xl border border-background-200 bg-background-50 p-5 md:p-6"
            >
              <h2 className="font-heading text-xl md:text-2xl">发送留言</h2>
              <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="contact-name" className="text-sm font-medium text-foreground-800">你的称呼</label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    maxLength={50}
                    placeholder="怎么称呼你"
                    className="mt-1.5 w-full rounded-lg border border-background-200 bg-background-50 px-3 py-2.5 text-sm outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="text-sm font-medium text-foreground-800">联系邮箱</label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    maxLength={100}
                    placeholder="you@example.com"
                    className="mt-1.5 w-full rounded-lg border border-background-200 bg-background-50 px-3 py-2.5 text-sm outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                  />
                </div>
              </div>
              <div className="mt-4">
                <label htmlFor="contact-subject" className="text-sm font-medium text-foreground-800">留言主题</label>
                <input
                  id="contact-subject"
                  name="subject"
                  type="text"
                  maxLength={100}
                  placeholder="想聊什么"
                  className="mt-1.5 w-full rounded-lg border border-background-200 bg-background-50 px-3 py-2.5 text-sm outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                />
              </div>
              <div className="mt-4">
                <label htmlFor="contact-message" className="text-sm font-medium text-foreground-800">留言内容</label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  placeholder="写下你想说的话…"
                  className="mt-1.5 w-full rounded-lg border border-background-200 bg-background-50 px-3 py-2.5 text-sm outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                ></textarea>
                <p className="mt-1 text-right text-xs text-foreground-400">最多 500 字</p>
              </div>

              <input
                type="text"
                name="contact_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="hp-guard-field"
              />

              <button
                type="submit"
                className="mt-5 inline-flex whitespace-nowrap items-center gap-1.5 rounded-lg bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600 transition-colors"
              >
                <i className="ri-send-plane-fill"></i>提交留言
              </button>

              {status === 'success' && (
                <p className="mt-3 rounded-lg bg-secondary-100 px-4 py-2.5 text-sm text-secondary-800">
                  留言已收到，感谢你的反馈，我们会尽快与你联系！
                </p>
              )}
              {status === 'error' && (
                <p className="mt-3 rounded-lg bg-primary-100 px-4 py-2.5 text-sm text-primary-800">
                  {formError}
                </p>
              )}
            </form>
          </div>
        </div>
      </section>
    </Layout>
  );
}